import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useFeatureFlag } from '../services/featureFlag.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Loader2 } from 'lucide-react';
import ApiService from '../services/api.service';
import { analysisService, type BuildOrchestration, type ModelStatus } from '../services/analysis.service';
import { toastService } from '../services/toast.service';
import { pipelineNodeService, type PipelineNode } from '../services/pipelineNode.service';
import { usePipelineNodes } from '../hooks/usePipelineNodes';
import { decimalFormatter } from '../services/formatting.service';
import { EditableRegion } from '../contexts/ReadOnlyContext';

export function getNodeStatusDisplay(status: string): string {
  switch (status) {
    case 'success': return 'COMPLETE';
    case 'error': return 'ERROR';
    case 'started': return 'RUNNING';
    case 'cancelled': return 'CANCELLED';
    case 'queued': return 'QUEUED';
    case 'skipped': return 'SKIPPED';
    default: return status.toUpperCase();
  }
}

export function getNodeStatusVariant(status: string): 'success' | 'destructive' | 'warning' | 'info' | 'secondary' | 'outline' | 'default' {
  switch (status) {
    case 'success': return 'success';
    case 'error': return 'destructive';
    case 'started': return 'info';
    case 'cancelled': return 'warning';
    case 'queued': return 'secondary';
    case 'skipped': return 'outline';
    default: return 'default';
  }
}

export function getOrchestrationStatusVariant(status: string): 'success' | 'destructive' | 'warning' | 'info' {
  switch (status) {
    case 'COMPLETE': return 'success';
    case 'ERROR': return 'destructive';
    case 'CANCELLED': return 'warning';
    default: return 'info';
  }
}

export interface BuildAllModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node?: PipelineNode;
  /** When provided (and no `node`), the modal builds all nodes of this source. */
  source?: { id: string; name: string };
  /** The source's SOURCE-node names, used to build the selector for a source build. */
  sourceNodeNames?: string[];
  defaultForce?: boolean;
  defaultCheckForNewRecords?: boolean;
  autoStart?: boolean;
  orchestrationId?: string;
}

type SelectorType = 'THIS' | 'UPSTREAM' | 'DOWNSTREAM' | 'ALL';
type SourceScope = 'SOURCE' | 'SOURCE_DOWNSTREAM';

/**
 * Starting a build is an action, not an edit to the node's configuration, so the
 * modal's options stay live in production mode. Only the fields you edit on the
 * detail pages themselves go read-only there.
 */
export function BuildAllModal(props: BuildAllModalProps) {
  return (
    <EditableRegion>
      <BuildAllModalContent {...props} />
    </EditableRegion>
  );
}

function BuildAllModalContent({ open, onOpenChange, node, source, sourceNodeNames, defaultForce = false, defaultCheckForNewRecords = false, autoStart = false, orchestrationId }: BuildAllModalProps) {
  const queryClient = useQueryClient();
  const [selector, setSelector] = useState<SelectorType>('THIS');
  const [sourceScope, setSourceScope] = useState<SourceScope>('SOURCE');
  const [force, setForce] = useState(false);
  const [checkForNewRecords, setCheckForNewRecords] = useState(false);
  const [runNodeExports, setRunNodeExports] = useState(false);
  const nodeExportsEnabled = useFeatureFlag('node-export', false);
  const [orchestration, setOrchestration] = useState<BuildOrchestration | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [showConfig, setShowConfig] = useState(true);

  // Modes: a `node` => single-node rebuild; a `source` (no node) => build all of a
  // source's nodes; neither => full Build All.
  const isSourceBuild = !node && !!source;
  const isBuildAll = !node && !source;

  // Fetch all nodes to create a lookup map for node names
  const { data: allNodesData } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
    enabled: open,
  });

  // The list above hides ephemeral nodes, but builds include them (dashboard
  // chart REPORT nodes), so look up whatever the build references that it lacks.
  const missingNodeIds = useMemo(() => {
    if (!allNodesData?.records || !orchestration?.model_statuses) return [];
    const known = new Set(allNodesData.records.map((n: PipelineNode) => n.id));
    const ids = Object.entries(orchestration.model_statuses)
      .map(([key, status]) => status.node_id || key)
      .filter(id => !known.has(id));
    return Array.from(new Set(ids)).sort();
  }, [allNodesData?.records, orchestration?.model_statuses]);

  const { data: missingNodes } = useQuery({
    queryKey: ['pipeline-node-names', missingNodeIds],
    queryFn: () => pipelineNodeService.findNamesByIds(missingNodeIds),
    enabled: open && missingNodeIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Create a map of node ID to node name/label
  const nodeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    [...(allNodesData?.records || []), ...(missingNodes || [])].forEach((n) => {
      map.set(n.id, n.label || n.name || n.id);
    });
    return map;
  }, [allNodesData?.records, missingNodes]);

  const autoStartedRef = useRef(false);

  const isViewMode = !!orchestrationId;

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelector('THIS');
      setSourceScope('SOURCE');
      setForce(defaultForce);
      setCheckForNewRecords(defaultCheckForNewRecords);
      setOrchestration(null);
      setIsLoading(false);
      setIsBuilding(false);
      setShowConfig(!autoStart && !isViewMode);
      autoStartedRef.current = false;
    }
  }, [open]);

  // Fetch existing orchestration when opened in view mode
  useEffect(() => {
    if (!open || !orchestrationId) return;
    let cancelled = false;

    setIsLoading(true);
    analysisService.getOrchestrationStatus(orchestrationId).then((result) => {
      if (cancelled) return;
      setOrchestration(result);
      setIsLoading(false);
      if (['RUNNING', 'QUEUED'].includes(result.status)) {
        setIsBuilding(true);
      }
    }).catch((err) => {
      if (cancelled) return;
      console.error('Failed to fetch orchestration:', err);
      setIsLoading(false);
      toastService.error('Failed to load build details');
    });

    return () => { cancelled = true; };
  }, [open, orchestrationId]);

  const orchestrationIdRef = useRef<string | null>(null);
  const pollingRef = useRef(false);

  // Poll orchestration status
  useEffect(() => {
    if (!orchestration || !isBuilding) {
      orchestrationIdRef.current = null;
      pollingRef.current = false;
      return;
    }

    if (orchestrationIdRef.current === orchestration.id && pollingRef.current) {
      return;
    }

    orchestrationIdRef.current = orchestration.id;
    pollingRef.current = true;
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const pollStatus = async () => {
      if (!isMounted || !orchestrationIdRef.current || !pollingRef.current) return;

      try {
        const updated = await analysisService.getOrchestrationStatus(orchestrationIdRef.current);

        if (!isMounted) return;

        setOrchestration(updated);

        if (['COMPLETE', 'ERROR', 'CANCELLED'].includes(updated.status)) {
          setIsBuilding(false);
          pollingRef.current = false;
          orchestrationIdRef.current = null;

          if (updated.status === 'COMPLETE') {
            queryClient.invalidateQueries();
            toastService.success('Build completed successfully');
          } else if (updated.status === 'ERROR') {
            if (updated.model_statuses) {
              Object.entries(updated.model_statuses).forEach(([key, modelStatus]) => {
                if (modelStatus.status === 'error') {
                  const nodeId = modelStatus.node_id || key;
                  queryClient.invalidateQueries({ queryKey: ['pipeline-node', nodeId] });
                }
              });
            }
            queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
            toastService.error('Build failed', {
              description: updated.error || 'An error occurred during the build',
            });
          } else if (updated.status === 'CANCELLED') {
            toastService.info('Build was cancelled');
          }
        } else {
          timeoutId = setTimeout(pollStatus, 1000);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error polling orchestration status:', err);
        setIsBuilding(false);
        pollingRef.current = false;
        orchestrationIdRef.current = null;
        toastService.error('Failed to check build status');
      }
    };

    timeoutId = setTimeout(pollStatus, 1000);

    return () => {
      isMounted = false;
      pollingRef.current = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [orchestration?.id, isBuilding, queryClient]);

  const handleStartBuild = useCallback(async () => {
    setIsLoading(true);
    setShowConfig(false);

    try {
      let result: { record: BuildOrchestration };

      if (isBuildAll) {
        result = await ApiService.getInstance().request(
          'POST',
          '/buildorchestrations/build/all',
          {
            force,
            check_for_records: checkForNewRecords,
            include_dashboard_dependencies: true,
            run_node_exports: runNodeExports,
          }
        ) as { record: BuildOrchestration };
      } else if (isSourceBuild) {
        // Build all of the source's nodes via the standard selector endpoint —
        // no dedicated backend route, so there's still one place builds enqueue.
        // Space-joined node names; a trailing "+" per token pulls downstream.
        const suffix = sourceScope === 'SOURCE_DOWNSTREAM' ? '+' : '';
        const selectorValue = (sourceNodeNames || [])
          .filter(Boolean)
          .map((name) => `${name}${suffix}`)
          .join(' ');
        if (!selectorValue) {
          throw new Error('This source has no buildable nodes.');
        }
        result = await ApiService.getInstance().request(
          'POST',
          '/buildorchestrations/build/selector',
          {
            selector: selectorValue,
            force,
            check_for_records: checkForNewRecords,
          }
        ) as { record: BuildOrchestration };
      } else {
        // Single-node rebuild. (The isBuildAll / isSourceBuild guards above mean
        // node is defined here, but the boolean aliases don't narrow it for TS.)
        const nodeName = node?.name || '';
        let selectorValue = '';
        switch (selector) {
          case 'THIS':
            selectorValue = nodeName;
            break;
          case 'UPSTREAM':
            selectorValue = '+' + nodeName;
            break;
          case 'DOWNSTREAM':
            selectorValue = nodeName + '+';
            break;
          case 'ALL':
            selectorValue = '+' + nodeName + '+';
            break;
        }

        result = await ApiService.getInstance().request(
          'POST',
          '/buildorchestrations/build/selector',
          {
            selector: selectorValue,
            force,
            check_for_records: checkForNewRecords,
          }
        ) as { record: BuildOrchestration };
      }

      setOrchestration(result.record);
      setIsBuilding(true);
      setIsLoading(false);

      if (result.record.status === 'COMPLETE') {
        setIsBuilding(false);
        queryClient.invalidateQueries();
        toastService.success('Build completed (no rebuild needed)');
      }
    } catch (err) {
      console.error('Failed to start build:', err);
      setIsLoading(false);
      setIsBuilding(false);
      toastService.error('Failed to start build', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
      setShowConfig(true);
    }
  }, [node, isBuildAll, isSourceBuild, source, sourceNodeNames, selector, sourceScope, force, checkForNewRecords, runNodeExports, queryClient]);

  // Auto-start build when autoStart is true
  useEffect(() => {
    if (open && autoStart && !autoStartedRef.current) {
      autoStartedRef.current = true;
      handleStartBuild();
    }
  }, [open, autoStart, handleStartBuild]);

  const handleCancel = useCallback(async () => {
    if (!orchestration || !isBuilding) return;

    try {
      await ApiService.getInstance().request(
        'DELETE',
        `/buildorchestrations/${orchestration.id}/cancel`
      );
    } catch (err) {
      console.error('Failed to cancel build:', err);
      toastService.error('Failed to cancel build');
    }
  }, [orchestration, isBuilding]);

  const handleClose = useCallback(() => {
    if (!isBuilding) {
      onOpenChange(false);
    }
  }, [isBuilding, onOpenChange]);

  // Get nodes from model_statuses
  const buildNodes = orchestration?.model_statuses
    ? Object.entries(orchestration.model_statuses).map(([nodeId, modelStatus]: [string, ModelStatus]) => {
        const actualNodeId = modelStatus.node_id || nodeId;
        const nodeName = nodeNameMap.get(actualNodeId) || modelStatus.node_path || actualNodeId;
        return {
          nodeId: actualNodeId,
          nodeName: nodeName,
          nodePath: modelStatus.node_path,
          status: modelStatus.status,
          error: modelStatus.error,
          startedAt: modelStatus.started_at,
          finishedAt: modelStatus.finished_at,
        };
      })
    : [];

  // Sort logs in reverse chronological order (newest first)
  const sortedLogs = useMemo(() => {
    if (!orchestration?.build_logs) {
      return [];
    }
    return [...orchestration.build_logs].sort((a, b) => b.seconds - a.seconds);
  }, [orchestration?.id, orchestration?.build_logs?.length]);

  const canClose = !isBuilding && (isViewMode || (orchestration && ['COMPLETE', 'ERROR', 'CANCELLED'].includes(orchestration.status)));

  const title = isViewMode
    ? 'Build Details'
    : (isBuildAll ? 'Build All Nodes' : (isSourceBuild ? 'Build Source' : 'Rebuild Node'));
  const configDescription = isBuildAll
    ? 'Configure the build options for a full rebuild of all nodes'
    : (isSourceBuild
      ? "Build all of this source's nodes, optionally including everything downstream"
      : 'Configure the build options and select which nodes to rebuild');
  const progressDescription = isViewMode
    ? 'Viewing build details'
    : (isBuildAll
      ? 'Tracking build progress for all nodes'
      : (isSourceBuild
        ? "Tracking build progress for this source's nodes"
        : 'Tracking build progress for all selected nodes'));

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!isBuilding || !newOpen) {
          onOpenChange(newOpen);
        }
      }}
    >
      <DialogContent
        className="sm:max-w-[800px] max-h-[90vh]"
        onPointerDownOutside={(e) => {
          if (isBuilding) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (isBuilding) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {showConfig ? title : 'Build Progress'}
          </DialogTitle>
          <DialogDescription>
            {showConfig ? configDescription : progressDescription}
          </DialogDescription>
        </DialogHeader>

        {showConfig ? (
          <DialogBody className="space-y-4 py-4 overflow-y-auto min-h-0">
            <div className="space-y-2">
              <Label htmlFor="scope">Build Scope</Label>
              {isBuildAll ? (
                <Input
                  id="scope"
                  value="All Nodes"
                  disabled
                />
              ) : isSourceBuild ? (
                <Select
                  value={sourceScope}
                  onValueChange={(value) => setSourceScope(value as SourceScope)}
                >
                  <SelectTrigger id="scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SOURCE">This source's nodes only</SelectItem>
                    <SelectItem value="SOURCE_DOWNSTREAM">This source's nodes + all downstream</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={selector}
                  onValueChange={(value) => setSelector(value as SelectorType)}
                >
                  <SelectTrigger id="scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="THIS">This node only</SelectItem>
                    <SelectItem value="UPSTREAM">This node + upstream nodes</SelectItem>
                    <SelectItem value="DOWNSTREAM">This node + downstream nodes</SelectItem>
                    <SelectItem value="ALL">This node + upstream + downstream</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="force">Force rebuild</Label>
                <p className="text-xs text-muted-foreground">
                  Rebuild even if no changes are detected
                </p>
              </div>
              <Switch
                id="force"
                checked={force}
                onCheckedChange={setForce}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="check-records">Check for new records</Label>
                <p className="text-xs text-muted-foreground">
                  Check source tables for new records before building
                </p>
              </div>
              <Switch
                id="check-records"
                checked={checkForNewRecords}
                onCheckedChange={setCheckForNewRecords}
              />
            </div>

            {isBuildAll && nodeExportsEnabled && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="run-node-exports">Run node exports</Label>
                  <p className="text-xs text-muted-foreground">
                    Export enabled nodes to SFTP after the build completes
                  </p>
                </div>
                <Switch
                  id="run-node-exports"
                  checked={runNodeExports}
                  onCheckedChange={setRunNodeExports}
                />
              </div>
            )}
          </DialogBody>
        ) : (
          <DialogBody className="space-y-4 py-4 overflow-y-auto min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Starting build...
                </span>
              </div>
            ) : orchestration ? (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Build Status</Label>
                    <Badge variant={getOrchestrationStatusVariant(orchestration.status)}>
                      {isBuilding && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                      {orchestration.status}
                    </Badge>
                  </div>
                  {orchestration.error && (
                    <p className="text-sm text-destructive">{orchestration.error}</p>
                  )}
                </div>

                <Tabs defaultValue="summary">
                  <TabsList>
                    <TabsTrigger value="summary">Summary</TabsTrigger>
                    <TabsTrigger value="logs">Logs</TabsTrigger>
                  </TabsList>
                  <TabsContent value="summary">
                    {buildNodes.length > 0 && (
                      <div className="space-y-2">
                        <Label>Nodes Being Built ({buildNodes.length})</Label>
                        <div className="border rounded-md">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[35%]">Node</TableHead>
                                <TableHead className="w-[65%]">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {buildNodes.map((buildNode, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">
                                      <div>
                                        <div>{buildNode.nodeName}</div>
                                        {buildNode.startedAt && (
                                          <div className="text-xs text-muted-foreground mt-0.5">
                                            Started: {new Date(buildNode.startedAt + (buildNode.startedAt.endsWith('Z') ? '' : 'Z')).toLocaleTimeString()}
                                          </div>
                                        )}
                                        {buildNode.finishedAt && (
                                          <div className="text-xs text-muted-foreground">
                                            Finished: {new Date(buildNode.finishedAt + (buildNode.finishedAt.endsWith('Z') ? '' : 'Z')).toLocaleTimeString()}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={getNodeStatusVariant(buildNode.status)}>
                                        {getNodeStatusDisplay(buildNode.status)}
                                      </Badge>
                                      {buildNode.error && (
                                        <p className="text-xs text-destructive mt-1">
                                          {buildNode.error}
                                        </p>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="logs">
                    {sortedLogs.length > 0 ? (
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[80px]">Time</TableHead>
                              <TableHead>Message</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedLogs.map((log, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-mono text-xs align-top whitespace-nowrap">
                                  {decimalFormatter(log.seconds)}s
                                </TableCell>
                                <TableCell className="text-xs font-mono break-all whitespace-pre-wrap">
                                  {log.message}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        No logs available yet.
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            ) : null}
          </DialogBody>
        )}

        <DialogFooter className="sticky bottom-0 bg-background border-t border-border">
          {showConfig ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleStartBuild} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  'Start Build'
                )}
              </Button>
            </>
          ) : (
            <>
              {isBuilding && (
                <Button variant="outline" onClick={handleCancel}>
                  Cancel Build
                </Button>
              )}
              {canClose && (
                <Button onClick={handleClose}>Close</Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
